import Check from '@/components/Atoms/Check'
import InputLabel from '@/components/Atoms/InputLabel'
import { useResponseId, useUrlType } from '@/components/hooks'
import { constants } from '@/constants'
import store, { RootState } from '@/reducers/store'
import { useAddNewField, useValidate } from '@/state/survey/formSlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
} from '@/state/survey/saveAnswerSlice'
import { Utility } from '@/utils'
import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import ValidationManager from '../../SurveyModel/fieldValidations'
import poaAnswerDetails from '../../SurveyModel/poaQuestions/poaAnswerDetails'
import { IAnswers } from '../../SurveyModel/types/commonTypes'
import { INode, nodeConfigurations } from '../../SurveyModel/types/nodeTypes'
import Textarea from '../Textarea/Textarea'

type Option = {
    label: string
    value: string
}

type MultiSelectCheckboxGroupProps = {
    options: string[]
    onChange: (selected: string[]) => void
    name: string
    node: INode
}

const AdvancedMultiChoiceCheckbox: React.FC<MultiSelectCheckboxGroupProps> = (
    props: MultiSelectCheckboxGroupProps
) => {
    const { node } = props
    const [selectedValues, setSelectedValues] = useState<string[]>([])
    const form = useSelector((state: RootState) => state.form)
    const validateInputField = useValidate()
    const [inputValue, setInputValue] = useState(form[node.name].value)
    const { isPatient } = useUrlType()
    const { answerResponseId } = useResponseId()
    const [updatesOptions, setUpdatedOptions] = useState<Option[]>([])
    const isInitialRunConfig = useRef(true)
    const addNewFieldValue = useAddNewField()
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    const isInitialRun = useRef(true)
    const [optionsWithExtraFields, setOptionsWithExtraFields] = useState<
        Record<string, INode>
    >({})

    useEffect(() => {
        if (form[node.name].value !== inputValue) {
            setInputValue(form[node.name].value)
        }
    }, [form, inputValue])

    useEffect(() => {
        const matchingNodes: Record<string, INode> = {}
        if (node.nodes && node.options && node.options.length) {
            node.nodes.forEach((Inode: INode) => {
                node.options?.forEach((option) => {
                    if (Inode.showIf && Inode.showIf.includes(`"${option}"`)) {
                        matchingNodes[option as string] = Inode
                    }
                })
            })
        }
        setOptionsWithExtraFields(matchingNodes)
    }, [node.nodes, node.options])

    useEffect(() => {
        if (
            node.options &&
            !updatesOptions.length &&
            node &&
            node.configurations &&
            isInitialRunConfig.current
        ) {
            const localOptopns: Option[] = []
            isInitialRunConfig.current = false
            node.options?.forEach((item) => {
                const option: Option = {
                    label: item as string,
                    value: item as string,
                }
                localOptopns.push(option)
            })

            setUpdatedOptions([...updatesOptions, ...localOptopns])
        }
    }, [updatesOptions, node, optionsWithExtraFields])

    useEffect(() => {
        if (isInitialRun.current && node) {
            const selectedValuesInitial: string[] = []
            isInitialRun.current = false
            if (
                typeof form[node.name]?.value === 'string' ||
                Array.isArray(form[node.name]?.value)
            ) {
                let nodeValue = form[node.name]?.value
                    ? Array.isArray(form[node.name]?.value)
                        ? form[node.name]?.value
                        : JSON.parse(form[node.name].value as string) // Safely cast to string
                    : []
                let nodeStringValues: string[] = []

                if (node.type === constants.MultiChoiceCheckbox) {
                    nodeStringValues = nodeValue
                } else {
                    if (nodeValue.length) {
                        nodeValue.forEach((element: IAnswers[]) => {
                            nodeStringValues.push(element[0].value)
                        })
                    }
                    let textValue: string = ''
                    if (nodeValue.length && node.options) {
                        textValue = _.difference(
                            nodeStringValues,
                            node.options as string[]
                        )[0] as string
                        nodeValue = _.intersection(
                            nodeStringValues,
                            node.options as string[]
                        )
                    }
                    const otherOption: nodeConfigurations | undefined =
                        node &&
                        node.configurations &&
                        node.configurations.find(
                            (Item) => Item.key === 'OtherOptionValue'
                        )

                    if (otherOption && node.nodes) {
                        node.nodes.forEach((Inode: INode) => {
                            if (
                                Inode.showIf &&
                                Inode.showIf.includes(`"${otherOption.value}"`)
                            ) {
                                if (!form[Inode.name]) {
                                    const otherNode: INode = Inode
                                    const detailsNodeForm =
                                        ValidationManager.applyCommonValidationsWithValue(
                                            otherNode,
                                            textValue ? textValue : ''
                                        )
                                    addNewFieldValue(
                                        Inode.name,
                                        detailsNodeForm
                                    )
                                    if (textValue) {
                                        nodeStringValues.push(otherOption.value)
                                    }
                                } else {
                                    validateInputField(
                                        Inode.name,
                                        textValue ? textValue : '',
                                        true
                                    )
                                    if (textValue) {
                                        nodeStringValues.push(otherOption.value)
                                    }
                                }
                            }
                        })
                    }
                }

                if (
                    Array.isArray(nodeStringValues) &&
                    nodeStringValues.length > 0
                ) {
                    nodeStringValues.map((checkItem: string) => {
                        selectedValuesInitial.push(checkItem)
                    })
                    setSelectedValues(selectedValuesInitial)
                } else {
                    setSelectedValues(selectedValuesInitial)
                }
            }
        }
    }, [node, isInitialRun, form])

    const handleOnBlur = () => {
        setTimeout(() => {
            // have tomaccess current state of the form.
            const form = store.getState().form
            if (!isPatient && answerResponseId) {
                const answers: BasicAnswerSave[] =
                    poaAnswerDetails.generateQuestionAnswerSubmitFormat(
                        form,
                        node
                    )
                updateAnswersDispatch(answers, answerResponseId)
            }
        }, 50) // or 50ms if needed
    }

    const handleChange = (value: string[]) => {
        if (node.validationDependants) {
            node.validationDependants.forEach((element: string) => {
                validateInputField(element, form[element].value, true)
            })
        }
        let formValue: string[] = []

        const noneOption: nodeConfigurations | undefined =
            node &&
            node.configurations &&
            node.configurations.find((Item) => Item.key === 'NoneOptionLabel')
        const otherOption: nodeConfigurations | undefined =
            node &&
            node.configurations &&
            node.configurations.find((Item) => Item.key === 'OtherOptionValue')

        const nodeValues = value.filter(
            (valueOprion) => valueOprion !== otherOption?.value
        )
        const selectedOptions = [...new Set(node.options as string[])].filter(
            (value) => nodeValues.includes(value)
        )

        if (
            node &&
            node.configurations &&
            noneOption &&
            value.includes(noneOption.value)
        ) {
            formValue = []
            formValue.push(noneOption.value as string)
        } else if (otherOption && value.includes(otherOption?.value)) {
            if (
                otherOption &&
                form[optionsWithExtraFields[otherOption?.value].name] &&
                form[optionsWithExtraFields[otherOption?.value].name].value
            ) {
                formValue = [
                    ...selectedOptions,
                    form[optionsWithExtraFields[otherOption?.value].name]
                        .value as string,
                ]
            }
        } else {
            formValue = [...selectedOptions]
        }
        const BloodThinningMedicinesList: BasicAnswerSave[][] = []

        formValue.forEach((item) => {
            const answerRow: BasicAnswerSave[] = []
            const ItemName: nodeConfigurations | undefined =
                node &&
                node.configurations &&
                node.configurations.find(
                    (Item) => Item.key === 'AnswerObjectProperty'
                )
            const listItem = {
                name: ItemName ? ItemName.value : '',
                value: item,
            }
            answerRow.push(listItem)
            BloodThinningMedicinesList.push(answerRow)
        })
        if (BloodThinningMedicinesList.length)
            validateInputField(
                node.name,
                JSON.stringify(BloodThinningMedicinesList),
                true
            )

        handleOnBlur()
    }

    const handleChangeOtherText = () => {
        handleChange(selectedValues)
    }

    const handleCheckboxChange = (value: string) => {
        let newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]
        const noneOption: nodeConfigurations | undefined =
            node &&
            node.configurations &&
            node.configurations.find((Item) => Item.key === 'NoneOptionLabel')
        if (
            node &&
            node.configurations &&
            noneOption &&
            noneOption.value &&
            value.includes(noneOption?.value)
        ) {
            if (selectedValues.includes(value)) {
                newSelectedValues = []
            } else {
                newSelectedValues = [value]
            }
        } else {
            newSelectedValues = newSelectedValues.filter(
                (item) => item !== noneOption?.value
            )
        }

        setSelectedValues(newSelectedValues)
        if (node.type === constants.MultiChoiceCheckbox) {
            validateInputField(
                node.name,
                JSON.stringify(newSelectedValues),
                true
            )
            handleOnBlur()
        } else {
            handleChange(newSelectedValues)
        }
    }

    return (
        <div
            // onBlur={handleOnBlur}
            className={`multichoice-wrap ${Utility.getClassForWidthPx(node)}  ${Utility.getClassForWidth(node)}  user-input multi-select-checkbox-group`}
        >
            {node.title ? (
                <InputLabel
                    label={node.title}
                    labelClasses={`main-title input-label ${!node.subTitle ? 'without-subtitle' : ''}`}
                ></InputLabel>
            ) : (
                ''
            )}
            {node.subTitle ? (
                <InputLabel
                    type="subTitle"
                    label={node.subTitle}
                    labelClasses="input-label"
                ></InputLabel>
            ) : (
                ''
            )}
            {/* <SingleChoice propClasses={'single-chocie-choices flex'} value={form[node.name].value} options={node.options} name={node.name} onChange={handleChange}/> */}
            <ul className={`${!isPatient ? 'flex flex-wrap' : ''}`}>
                {updatesOptions.map((option, index) => (
                    <li className={`${!isPatient ? 'w-1/4' : ''}`} key={index}>
                        <Check
                            name={`checkbox${index}`}
                            onChange={handleCheckboxChange}
                            value={option.value}
                            label={option.label}
                            selectedValue={selectedValues.includes(
                                option.value
                            )}
                        />

                        {optionsWithExtraFields[option.value] &&
                        selectedValues.includes(option.value) ? (
                            <div>
                                <Textarea
                                    node={optionsWithExtraFields[option.value]}
                                    name={
                                        optionsWithExtraFields[option.value]
                                            .name
                                    }
                                    onBlur={handleChangeOtherText}
                                    propClasses={`display-within-checkbox`}
                                />
                            </div>
                        ) : (
                            ''
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}

export default AdvancedMultiChoiceCheckbox
